import React, { useCallback, useEffect, useState, Component } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
// Import all slides
import { TitleSlide } from './components/slides/TitleSlide';
import { ProblemSlide } from './components/slides/ProblemSlide';
import { FPGAAdvantageSlide } from './components/slides/FPGAAdvantageSlide';
import { HowItWorksSlide } from './components/slides/HowItWorksSlide';
import { PerformanceSlide } from './components/slides/PerformanceSlide';
import { RealTimeSlide } from './components/slides/RealTimeSlide';
import { RoadmapSlide } from './components/slides/RoadmapSlide';
import { UseCasesSlide } from './components/slides/UseCasesSlide';
import { IntegrationSlide } from './components/slides/IntegrationSlide';
import { TeamSlide } from './components/slides/TeamSlide';
import { GetStartedSlide } from './components/slides/GetStartedSlide';
// Appendix slides
import { AppendixCoverSlide } from './components/slides/AppendixCoverSlide';
import { AppendixA1Slide } from './components/slides/AppendixA1Slide';
import { AppendixA2Slide } from './components/slides/AppendixA2Slide';
import { AppendixA3Slide } from './components/slides/AppendixA3Slide';
const slides = [TitleSlide, ProblemSlide, FPGAAdvantageSlide, HowItWorksSlide, PerformanceSlide, RealTimeSlide, RoadmapSlide, UseCasesSlide, IntegrationSlide, TeamSlide, GetStartedSlide,
// Appendix
AppendixCoverSlide, AppendixA1Slide, AppendixA2Slide, AppendixA3Slide];
export function App() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, slides.length - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = slides[currentIndex];
  return <div className="w-full h-screen bg-brand-bg text-brand-text flex flex-col font-sans selection:bg-brand-teal/30 selection:text-brand-text" data-id="element-0">
      {/* Main Slide Content */}
      <main className="flex-1 relative overflow-y-auto" data-id="element-1">
        <AnimatePresence mode="wait" data-id="element-2">
          <CurrentSlideComponent key={currentIndex} data-id="element-3" />
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      <footer className="h-16 border-t border-brand-border bg-brand-bg/80 backdrop-blur-md flex items-center justify-between px-6 z-50" data-id="element-4">
        <div className="flex items-center gap-4 w-1/3" data-id="element-5">
          <button onClick={prevSlide} disabled={currentIndex === 0} className="p-2 rounded-full hover:bg-brand-bg-card text-brand-text-dim hover:text-brand-teal disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-brand-text-dim transition-colors" aria-label="Previous slide" data-id="element-6">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-7" />
          </button>
          <button onClick={nextSlide} disabled={currentIndex === slides.length - 1} className="p-2 rounded-full hover:bg-brand-bg-card text-brand-text-dim hover:text-brand-teal disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-brand-text-dim transition-colors" aria-label="Next slide" data-id="element-8">
            <ChevronRightIcon className="w-6 h-6" data-id="element-9" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 w-1/3" data-id="element-10">
          {slides.map((_, idx) => <button key={idx} onClick={() => goToSlide(idx)} className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-brand-teal w-6' : 'bg-brand-border hover:bg-brand-text-dim'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-11" />)}
        </div>

        <div className="w-1/3 text-right font-mono text-sm text-brand-text-dim" data-id="element-12">
          {currentIndex + 1} / {slides.length}
        </div>
      </footer>
    </div>;
}